import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../lib/apiClient";
import type { AuditAccessGrant, RevokeAuditGrant } from "../types/audit";

/**
 * Fetch auditor's active grants
 */
export function useAuditorGrants() {
  return useQuery({
    queryKey: ["auditor-grants"],
    queryFn: async () => {
      const { data } = await api.get("/audit/active-grants");
      return data.data as AuditAccessGrant[];
    },
  });
}

/**
 * Check if auditor has grant for specific facility.
 *
 * The endpoint answers a FAILED check with the sentinel `{ hasAccess: false }`
 * rather than null (backend AC H-3, so a refusal cannot be mistaken for "no
 * data"). That sentinel is TRUTHY, so every consumer's `grant && ...` /
 * `grant?.foo` guard passes and then dereferences fields the sentinel does not
 * carry — `access_scopes.some(...)` threw here and white-screened the facility
 * audit page (F-081).
 *
 * Normalise at the boundary so the rest of the app keeps the contract it is
 * already written against: a grant, or null. Do not push this check into
 * callers — there are several, and each would have to repeat it.
 */
export function useAuditorGrant(facilityId: string) {
  return useQuery({
    queryKey: ["auditor-grant", facilityId],
    queryFn: async () => {
      const { data } = await api.get(`/audit/grants/check/${facilityId}`);
      const payload = data.data as
        AuditAccessGrant | { hasAccess: false } | null;

      // No grant: the sentinel, or anything that is not a usable grant row.
      if (!payload || !("access_scopes" in payload)) return null;

      return payload;
    },
    enabled: !!facilityId,
  });
}

/**
 * Revoke audit access grant (managers only)
 */
export function useRevokeAuditGrant(grantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // US-081: error fallback centralized here; audit management catch is sole caller.
    meta: { errorFallback: "Failed to revoke audit access" },
    mutationFn: async (revocation: RevokeAuditGrant) => {
      const { data } = await api.put(
        `/audit/access-grants/${grantId}/revoke`,
        revocation,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auditor-grants"] });
      queryClient.invalidateQueries({ queryKey: ["auditor-grant"] });
    },
  });
}
